import { TabList, Tabs } from '@cad-arc';
import React, { PropsWithChildren, useState, useEffect, useCallback } from 'react';
import { useMessagingStore } from '../../hooks/use-messaging-store';
import { observer } from 'mobx-react';
import isHotkey from 'is-hotkey';
import { ConversationFilterTab } from './filter-tab';
import { ConversationFilterTabMenu } from './conversation-filter-tab-menu';
import { isFilterTabMenu } from '../../helpers/conversations-filter-helpers';
import { ConversationsFilter, FilterTabMenuType } from '../../types';

const ConversationFilterTabs: React.FC<
    PropsWithChildren<{
        selectedConversationFilter?: ConversationsFilter;
        defaultFocusElementRef?: React.MutableRefObject<HTMLDivElement | null>;
    }>
> = ({ children, selectedConversationFilter, defaultFocusElementRef }) => {
    return (
        <Tabs
            data-testid="conversation-filter-tabs"
            defaultValue="ALL_ACTIVE"
            value={selectedConversationFilter?.filterType}
        >
            <TabList ref={defaultFocusElementRef}>{children}</TabList>
        </Tabs>
    );
};

export const ConversationsFilterBar: React.FC<{
    defaultFocusElementRef?: React.MutableRefObject<HTMLDivElement | null>;
    setShowAllConversations?: (value: boolean) => void;
}> = observer(({ defaultFocusElementRef, setShowAllConversations }) => {
    const {
        participantIdentities,
        dispatchAreasUnreadInfo,
        selectedConversationFilter,
        activeFilterTabMenu,
    } = useMessagingStore();

    const [isHoldingCtrlKey, setIsHoldingCtrlKey] = useState(false);

    useEffect(() => {
        const onKeyDown = (event: KeyboardEvent) => {
            if (isHotkey('mod', event) && !isHoldingCtrlKey) {
                setIsHoldingCtrlKey(true);
            }
        };

        const onKeyUp = (event: KeyboardEvent) => {
            if (!isHotkey('mod', event) && isHoldingCtrlKey) {
                setIsHoldingCtrlKey(false);
            }
        };

        document.addEventListener('keydown', onKeyDown);
        document.addEventListener('keyup', onKeyUp);

        return () => {
            document.removeEventListener('keydown', onKeyDown);
            document.removeEventListener('keyup', onKeyUp);
        };
    }, [isHoldingCtrlKey]);

    const clearMenuOpenShortcutPress = useCallback(() => {
        if (isHoldingCtrlKey) {
            setIsHoldingCtrlKey(false);
        }
    }, [isHoldingCtrlKey]);

    if (participantIdentities === undefined || dispatchAreasUnreadInfo === undefined) {
        // data hasn't loaded yet
        return <ConversationFilterTabs />;
    }

    /**
     * activeFilterTabMenu.tab indicates which roup tab menu is active/selected. It doesn't mean the menu is open.
     * To show the menu, user has to
     * click the tab menu which will set activeFilterTabMenu.isOpen to true. Click tab menu again, or click outside will set isOpen to false.
     * OR hold ctrl key which will open menu for activeFilterTabMenu.tab
     */
    const getIsFilterTabMenuOpen = (filterTabMenuType: FilterTabMenuType) => {
        return (
            !!activeFilterTabMenu &&
            activeFilterTabMenu.tab === filterTabMenuType &&
            (activeFilterTabMenu.isOpen ||
                (isFilterTabMenu(activeFilterTabMenu.tab) && isHoldingCtrlKey))
        );
    };

    return (
        <ConversationFilterTabs
            selectedConversationFilter={selectedConversationFilter}
            defaultFocusElementRef={defaultFocusElementRef}
        >
            <ConversationFilterTab
                filter={{ filterType: 'ALL_ACTIVE', filterParticipant: null }}
                setShowAllConversations={setShowAllConversations}
            />
            <ConversationFilterTab
                filter={{
                    filterType: 'USER',
                    filterParticipant: participantIdentities.activeMappedByType.USER,
                }}
                setShowAllConversations={setShowAllConversations}
            />
            {participantIdentities.activeMappedByType.UNIT && (
                <ConversationFilterTab
                    filter={{
                        filterType: 'UNIT',
                        filterParticipant: participantIdentities.activeMappedByType.UNIT,
                    }}
                    setShowAllConversations={setShowAllConversations}
                />
            )}
            {participantIdentities.activeMappedByType.CALL_TAKER_STATION && (
                <ConversationFilterTab
                    filter={{
                        filterType: 'CALL_TAKER_STATION',
                        filterParticipant:
                            participantIdentities.activeMappedByType.CALL_TAKER_STATION,
                    }}
                    setShowAllConversations={setShowAllConversations}
                />
            )}
            {participantIdentities.activeMappedByType.DISPATCH_AREA_RESPONDERS && (
                <ConversationFilterTab
                    filter={{
                        filterType: 'DISPATCH_AREA_RESPONDERS',
                        filterParticipant:
                            participantIdentities.activeMappedByType.DISPATCH_AREA_RESPONDERS,
                    }}
                    setShowAllConversations={setShowAllConversations}
                />
            )}
            <ConversationFilterTabMenu
                label="Dispatch Areas"
                filterTabMenuType="DISPATCH_AREA"
                participants={participantIdentities.activeMappedByType.DISPATCH_AREA}
                isMenuOpen={getIsFilterTabMenuOpen('DISPATCH_AREA')}
                clearMenuOpenShortcutPress={clearMenuOpenShortcutPress}
                setShowAllConversations={setShowAllConversations}
            />
            <ConversationFilterTabMenu
                label="Inactive"
                filterTabMenuType="INACTIVE"
                participants={participantIdentities.inactive}
                isMenuOpen={getIsFilterTabMenuOpen('INACTIVE')}
                clearMenuOpenShortcutPress={clearMenuOpenShortcutPress}
                setShowAllConversations={setShowAllConversations}
            />
        </ConversationFilterTabs>
    );
});
